import { GlobalState, DomUtils } from '../types/common';
import { getCollectionHandle } from '../utils/product';
import {
  AIFilter,
  CollectionState, Filters, Query, selectedAIFilter, SortList
} from '../types/collection';
import _ from 'lodash';
import React from 'react';
import { collectionGAEvents } from '../hooks/services/curatedCollection';

export const STYLES_TO_COLORS = {
  bge: 'beige',
  blk: 'black',
  blu: 'blue',
  brn: 'brown',
  gld: 'gold',
  grn: 'green',
  gry: 'grey',
  mul: 'multi',
  nvy: 'navy',
  org: 'orange',
  pnk: 'pink',
  pur: 'purple',
  red: 'red',
  wht: 'white',
  yel: 'yellow'
};

// reverse of STYLES to COLORS
const COLORS_TO_STYLES = _.invert(STYLES_TO_COLORS);
const DELIMITERS = {
  global: '-',
  size: 'size',
  rating: 'rating',
  vendor: 'brands'
};
const TYPES = {
  color: 'color',
  size: 'size',
  vendor: 'vendor',
  rating: 'rating'
};
const STORAGE_KEYS = {
  productsPerPage: 'products_per_page',
  vendor: 'isVendorMinimized',
  color: 'isColorMinimized',
  size: 'isSizeMinimized',
  rating: 'isRatingMinimized',
  filter: 'isFilterMinimized'
};
const AI_FILTER_KEYS = {
  filterHeading: 'Filter By',
  sortHeading: 'Sort By',
  sortFilterMobileCtaText: 'Sort & Filter'
};
const MappedFilterValue = {
  hemlinelength: 'HemlineLength'
};
/**
 * @method displayWait
 * @description Get the collection handle of this particular search.
 */
const displayWait = (): void => {
  const overlay = document.querySelector('#page-overlay');
  const throbber = document.querySelector('.gb-throbber');

  if (overlay) {
    overlay.classList.add('active');
  }

  if (throbber) {
    throbber.classList.remove('hide');
  }
};
/**
 * @method removeWait
 * @description Get the collection handle of this particular search.
 */
const removeWait = (): void => {
  const overlay = document.querySelector('#page-overlay');
  const throbber = document.querySelector('.gb-throbber');

  if (overlay) {
    overlay.classList.remove('active');
  }

  if (throbber) {
    throbber.classList.add('hide');
  }
};
/**
 * Add/remove the filter value from particular section.
 * @param filter - type of the filter.
 * @param value - value of the filter.
 * @returns {*} - updated filter object.
 * @function filterAppender.
 */
const filterAppender = (filterType, value): void => {
  let index;
  let filter = filterType;

  index = filter.indexOf(value);

  if (value === 'size-0' && filter[index + value.length] === '0') {
    index = filter.indexOf(value, index + value.length + 1);
  }

  if (index !== -1) {
    if (filter.charAt(index - 1) === DELIMITERS.global) {
      filter = filter.replaceBetween(index - 1, index + value.length, '');
    } else if (filter.charAt(index + value.length) === DELIMITERS.global) {
      filter = filter.replaceBetween(index, index + 1 + value.length, '');
    } else {
      filter = filter.replaceBetween(index, index + value.length, '');
    }
  } else if (filter === '') {
    filter = value;
  } else {
    filter += DELIMITERS.global + value;
  }

  return filter;
};
/**
 * Cut the url and return the type specific url sections.
 * @param {String} [path] - specific url for stubbing defaults to location.pathname.
 * @returns {{sizes: string, colors: string, vendors: string}}
 * @method urlCutter.
 */
// eslint-disable-next-line complexity
const urlCutter = (urlPath: string, domUtils: DomUtils): any => {
  let vendors = '';
  let colors = '';
  let sizes = '';
  let ratings = '';
  let path = urlPath;
  let regexForRating = new RegExp(DELIMITERS.rating + DELIMITERS.global + '\\d?(-|$)', 'gi');
  let regexForSize = new RegExp(DELIMITERS.size + DELIMITERS.global + '\\w+(-|$)', 'gi');
  let temp;
  let tempWithoutRating;
  let len;

  if (!path) {
    path = window.location.pathname;
  }

  // eslint-disable-next-line no-useless-escape
  if (!window.location.href.match(/\/collections\/([^\/]*)/gi)) {
    if (path.split('/').length === 5) {
      path = path.substring(path.lastIndexOf('/') + 1);

      if (path) {
        path = decodeURIComponent(path).split(' ').join('');
      }
    } else {
      path = '';
    }
  } else {
    path = domUtils.getURLParameter('filters');

    if (path && path.split('?')[0]) {
      path = decodeURIComponent(path.split('?')[0]).split(' ').join('');
    }
  }

  if (path) {
    tempWithoutRating = path.replace(regexForRating, '');
    ratings = path.replace(tempWithoutRating, '');
    temp = tempWithoutRating.split(DELIMITERS.vendor);

    if (temp && temp.length === 2) {
      vendors = DELIMITERS.vendor + temp[1];
    }

    len = vendors.length;

    if (vendors.charAt(len - 1) === DELIMITERS.global) {
      vendors = vendors.substring(0, len - 1);
    }

    colors = temp[0].replace(regexForSize, '');
    sizes = temp[0].replace(colors, '');
    len = colors.length;

    if (colors.charAt(len - 1) === DELIMITERS.global) {
      colors = colors.substring(0, len - 1);
    }

    len = sizes.length;

    if (sizes.charAt(len - 1) === DELIMITERS.global) {
      sizes = sizes.substring(0, len - 1);
    }
  }

  return {
    sizes: sizes,
    colors: colors.toLowerCase(),
    vendors: vendors.toLowerCase(),
    ratings: ratings
  };
};
/**
 * Assemble the url based on the filters selected.
 * @param assembly - assembly object.
 * @returns {string|*} - new url based on the assembly.
 * @method urlAssembler.
 */
const urlAssembler = (assembly): string => {
  let filters = assembly.sizes;
  let url = assembly.prefix;

  if (filters !== '' && assembly.colors !== '') {
    filters += '-' + assembly.colors;
  } else {
    filters += assembly.colors;
  }

  if (filters !== '' && assembly.vendors !== '') {
    filters += '-' + assembly.vendors;
  } else {
    filters += assembly.vendors;
  }

  if (filters !== '' && assembly.ratings !== '') {
    filters += '-' + assembly.ratings;
  } else {
    filters += assembly.ratings;
  }

  if (filters !== '') {
    url += '/' + filters;
  }

  if (assembly.query) {
    assembly.query = assembly.query.replace(/pnum=\d+/gi, '').replace('?&', '?').replace(/\?$/, '');
  }

  if (assembly.query && assembly.query !== '') {
    url += assembly.query;
  }

  return url;
};
/**
 * Build the url on some event.
 * @param type - selected/deselected filter type.
 * @param filterValue - value of the filter.
 * @param globalContext - value of the global context.
 * @returns url.
 * @method urlBuilder.
 */
const urlBuilder = (type: string, filterValue: string, globalContext): string => {
  const path = window.location.href;
  const pathname = window.location.pathname;
  let assembly;
  let value = filterValue;

  assembly = urlCutter(pathname, globalContext.gb.domUtils);

  if (type === 'color') {
    assembly.colors = filterAppender(assembly.colors, value);
  } else if (type === 'size') {
    value = DELIMITERS.size + DELIMITERS.global + value;
    assembly.sizes = filterAppender(assembly.sizes, value);
  } else if (type === 'vendor') {
    if (assembly.vendors === '') {
      assembly.vendors = DELIMITERS.vendor;
    }

    assembly.vendors = filterAppender(assembly.vendors, value);

    if (assembly.vendors === DELIMITERS.vendor) {
      assembly.vendors = '';
    }
  } else if (type === 'rating') {
    value = DELIMITERS.rating + DELIMITERS.global + value;
    assembly.ratings = filterAppender(assembly.ratings, value);
  }

  if (pathname.split('/').length === 5) {
    assembly.prefix = path.substring(0, path.lastIndexOf('/'));
  } else if (path.indexOf('?') !== -1) {
    assembly.prefix = path.substring(0, path.lastIndexOf('?'));
  } else {
    assembly.prefix = path;
  }

  assembly.query = window.location.search;

  // encode vendors for special characters
  assembly.vendors = encodeURIComponent(assembly.vendors);

  return urlAssembler(assembly);
};
/**
 * @method displayWait
 * @description Get the collection handle of this particular search.
 */
const onFilterChange = (type, filter, globalContext): string => urlBuilder(type, filter, globalContext);
/**
 * parse the url and return the filters.
 * @param {String} [path] specific url for stubbing.
 * @returns {{sizes: Array, colors: Array, vendors: Array}}.
 * @function urlParser.
 */
const urlParser = (globalContext: GlobalState, path?: string): any => {
  const parsedURL = urlCutter(path, globalContext.gb.domUtils);
  const filterObj = {
    sizes: [],
    colors: [],
    vendors: [],
    ratings: []
  };

  if (parsedURL.sizes !== '') {
    let decodedSizes: any = decodeURIComponent(parsedURL.sizes);

    // need to check how to allow "replaceAll" method for strings
    filterObj.sizes = decodedSizes.replaceAll(`${DELIMITERS.size + DELIMITERS.global}`, '').split(DELIMITERS.global);
  }

  if (parsedURL.colors !== '') {
    filterObj.colors = decodeURIComponent(parsedURL.colors).split(DELIMITERS.global);
  }

  if (parsedURL.vendors !== '') {
    let decodedVendors: any = decodeURIComponent(parsedURL.vendors);

    filterObj.vendors = decodedVendors.replaceAll(`${DELIMITERS.vendor + DELIMITERS.global}`, '').split(DELIMITERS.global);
  }

  if (parsedURL.ratings !== '') {
    let decodedSizes: any = decodeURIComponent(parsedURL.ratings);

    filterObj.ratings = decodedSizes.replaceAll(`${DELIMITERS.rating + DELIMITERS.global}`, '').split(DELIMITERS.global);
  }

  return filterObj;
};
/**
 * Capitalize the first letter of each word in a string.
 * @param str - string to be capitalized.
 * @returns {XML|*|string|void} - capitalized string.
 * @function capitalize.
 */
const capitalize = (str: string): string => str.replace(/(?:^|\s)\S/g, function (a) {
  return a.toUpperCase();
});
/**
 * Check whether at least one filter is selected in a particular sections.
 * @param {String} type - the section whose filter needs to be checked.
 * @return {boolean} - true if atleast one filter is selected, false otherwise.
 */
const hasFilterSelected = (type, CollectionContextValue): boolean => {
  const { collectionContext } = CollectionContextValue;

  if (_.isString(TYPES[type])) {
    return !!collectionContext['selected' + capitalize(type) + 's'].length;
  }

  return false;
};
/**
 * Clear the filters of a particular/all section.
 * @param {String} [filterToClear] - section to clear / all.
 * @returns {String} - url without the particular/all filters.
 * @method urlClearFilters.
 */
const urlClearFilters = (filterToClear, globalContext): string => {
  let path = window.location.href;
  let pathname = window.location.pathname;
  let assembly;

  assembly = urlCutter(pathname, globalContext.gb.domUtils);

  if (filterToClear === 'color') {
    assembly.colors = '';
  } else if (filterToClear === 'size') {
    assembly.sizes = '';
  } else if (filterToClear === 'vendor') {
    assembly.vendors = '';
  } else if (filterToClear === 'rating') {
    assembly.ratings = '';
  } else {
    assembly.sizes = '';
    assembly.colors = '';
    assembly.vendors = '';
    assembly.ratings = '';
  }

  if (pathname.split('/').length === 5) {
    assembly.prefix = path.substring(0, path.lastIndexOf('/'));
  } else if (path.indexOf('?') !== -1) {
    assembly.prefix = path.substring(0, path.lastIndexOf('?'));
  } else {
    assembly.prefix = path;
  }

  assembly.query = window.location.search;

  return urlAssembler(assembly);
};
const clearFilter = (type, collectionContextValue): void => {
  const { collectionContext } = collectionContextValue;

  if (_.isString(TYPES[type])) {
    const key: string = 'selected' + capitalize(type) + 's';

    collectionContextValue.setCollectionContext({
      ...collectionContext,
      [key]: []
    });
  }
};
const getPageSize = (data): number => data.correctedPageSize || data.pageSize;
/**
* Set the total number of pages for the collection handle silently.
* @method setTotalPages.
*/
const setTotalPages = (data): void => {
  const pageSize = getPageSize(data);
  let totalPages = 1;

  if (pageSize) {
    totalPages = Math.ceil((data.totalProducts / pageSize));
  }

  data.totalPages = totalPages;
};
/**
 * correct the page size if we need to show inline editorial content.
 * @param {Object} data - to be modified.
 * @private
 */
const correctPageSize = (data): void => {
  if (data && _.isNumber(data.pageSize) && data.testingDFPInline !== '0' && data.showInlineEditorialContent) {
    data.correctedPageSize = data.pageSize - 1;
  } else {
    data.correctedPageSize = data.pageSize;
  }
};
/**
 * Method to set static bread crumbs for fall collection
 * @method getCollectionBreadCrumb
 */
// eslint-disable-next-line consistent-return
const getCollectionBreadCrumb = (config, collectionHandle): string => {
  if (config.collectionWithEditorialContent && config.collectionWithEditorialContent[collectionHandle]) {
    if (collectionHandle === '70s-boho-trend' || collectionHandle === 'fall-florals-trend' || collectionHandle ===
                'pops-of-color-trend') {
      return 'fall trends/' + collectionHandle.replace(/-/g, ' ');
    }

    return collectionHandle.replace(/-/g, ' ');
  }
};
/**
 * Set the max show able page based on the returned products parameter silently.
 * @method setMaxShowAblePage.
 */
const setMaxShowAblePage = (data): void => {
  const returnedProducts = data.returnedProducts;
  const totalProducts = data.totalProducts;
  let maxShowAble;

  if (returnedProducts === totalProducts) {
    maxShowAble = data.totalPages;
  } else {
    maxShowAble = Math.floor(returnedProducts / getPageSize(data));
  }

  data.maxShowAblePage = maxShowAble;
};
/**
 * Method to handle the data for editorial content. This method sets the data silently.
 * @method handleEditorialData
 */
// eslint-disable-next-line complexity
const handleEditorialData = (): any => {
  const collectionHandle = getCollectionHandle();
  const config = window.gb.config;
  const showEditorialContent = !!(config.collectionWithEditorialContent && config.collectionWithEditorialContent[
    collectionHandle]);
  const showBannerThroughShopify = !!(config.collectionWithShopifyBanner && config.collectionWithShopifyBanner[
    collectionHandle]);
  const donotShowBannerThroughShopify = !!(config.collectionWithoutShopifyBanner &&
    config.collectionWithoutShopifyBanner[collectionHandle]);
  const showBannerThroughShopifyFiles = !!(config.collectionWithShopifyFilesBanner &&
      config.collectionWithShopifyFilesBanner[collectionHandle]);
  const showInlineEditorialContent = !!(config.collectionPageInlineEditorialContent &&
        config.collectionWithInlineEditorialContent[collectionHandle]);
  let data = {
    showEditorialContent: null,
    showInlineEditorialContent: showInlineEditorialContent,
    showBanner: true,
    showShopifyFilesBanner: true,
    shopifyFilesBannerSrcDesktop: '',
    shopifyFilesBannerSrcMobile: '',
    subCollections: false,
    collectionBreadCrumb: '',
    collectionBreadCrumbHref: '',
    subCollectionBreadCrumb: ''
  };
  let collectionBreadCrumb;

  if (!showBannerThroughShopify) {
    data.showBanner = false;
  }

  if (donotShowBannerThroughShopify) {
    data.showBanner = false;
  }

  if (!showBannerThroughShopifyFiles) {
    data.showShopifyFilesBanner = false;
  } else {
    data.shopifyFilesBannerSrcDesktop = 'banner_' + collectionHandle + '_desktop.jpg';
    data.shopifyFilesBannerSrcMobile = 'banner_' + collectionHandle + '_mobile.jpg';
  }

  // no carousel images
  if (showEditorialContent && window.testingDFPCarousel === '0') {
    data.showEditorialContent = 'noImage';
  } else {
    data.showEditorialContent = false;
  }

  if (showEditorialContent) {
    collectionBreadCrumb = getCollectionBreadCrumb(config, collectionHandle);

    if (collectionBreadCrumb.indexOf('/') !== -1) {
      data.subCollections = true;
      data.collectionBreadCrumb = collectionBreadCrumb.split('/')[0];
      data.collectionBreadCrumbHref = 'fall-trends';
      data.subCollectionBreadCrumb = collectionBreadCrumb.split('/')[1];
    } else {
      data.collectionBreadCrumb = collectionBreadCrumb;
    }
  }

  return data;
};
/**
 * a. If default version - Convert the size(s) to GB size(s).
 * b. If version is V1 - Convert the size(s) to canonical size(s) & GB size(s).
 * Gives the converted size(s) in result.
 * @param {Number|String|Array} size - size(s).
 * @returns {Array|Number} - GB size(s).
 * @function convertSizeToGBSize.
 */
const convertSizeToGBSize = (size): any => {
  const sizes = [];
  const len = size.length;
  let i;

  for (i = 0; i < len; i += 1) {
    sizes.push(size[i]);
  }

  return sizes;
};
/**
 * Modify the colors into the required format.
 * @param {String|Array} color - to be converted.
 * @param {Boolean} forBackEnd - true if we are modifying for BackEnd interaction.
 * @return {String|Array} the modified color.
 * @function modifyColors
 */
// eslint-disable-next-line consistent-return
const modifyColors = (color: string | string[], forBackEnd: boolean): string | string[] => {
  if (_.isString(color)) {
    return (forBackEnd ? COLORS_TO_STYLES[color.toLowerCase()] : STYLES_TO_COLORS[color.toLowerCase()]) || color;
  }

  if (_.isArray(color)) {
    return _.map(color, function (currentColor: string) {
      return modifyColors(currentColor, forBackEnd) as string;
    });
  }
};
const modifyRatings = (rating): any => {
  if (_.isString(rating)) {
    return rating;
  }

  if (_.isArray(rating)) {
    return _.map(rating, function (ratingFilter) {
      return modifyRatings(ratingFilter);
    });
  }

  return rating;
};
/**
 * Modify the vendors into the required format.
 * @param {String|Array} vendors - the vendors to be modified.
 * @param {Boolean} [forBE] - true if we are modifying for BackEnd interaction.
 * @return {Array} containing the modified vendors.
 * @function modifyVendors.
 */
const modifyVendors = (vendors: string | string[], forBE: boolean): any => {
  if (_.isString(vendors)) {
    if (forBE) {
      return (String(vendors).toLowerCase()).split('_').join(' ');
    }

    return (String(vendors).toLowerCase()).split(' ').join('_');
  }

  if (_.isArray(vendors)) {
    return _.map(vendors, function (vendor) {
      return modifyVendors(vendor, forBE);
    });
  }

  return vendors;
};
/**
 * a. If default version Convert the GB size(s) to normal size(s).
 * b. If version is V1 Convert the canonical size(s) to normal size(s).
 * Gives the converted size(s) in result.
 * @param gbSize {Number|String|Array} representing Gb size(s).
 * @returns {Array|String} containing size(s).
 * @function convertGBSizeToSize.
 */
const convertGBSizeToSize = (gbSize): any => {
  if (_.isString(gbSize)) {
    return gbSize.toUpperCase();
  }

  if (_.isArray(gbSize)) {
    return _.map(gbSize, convertGBSizeToSize);
  }

  return gbSize;
};
/**
 * Modify the product information obtained in the search api.
 * Does things like adding specific product image, changing url etc.
 * @param data - the data fetched from api.
 * @param collectionHandle - the present collectionHandle.
 * @param filters - the filter section of the url.
 * @function modifyProductInfo.
 */
const setSelected = (type, data?): any => {
  let available;
  let selected;
  let modified;
  let global;
  let modify;
  let filters: Filters = [];
  let i;
  let len;
  let select;
  let disable;

  if (type === TYPES.color) {
    global = data.allColors;
    available = data.availableColors;
    selected = data.selectedColors;
    modified = data.modifiedColors;
    modify = modifyColors;
  } else if (type === TYPES.size) {
    global = data.allSizes;
    available = data.availableSizes;
    selected = data.selectedSizes;
    modified = data.modifiedSizes;
    modify = convertGBSizeToSize;
  } else if (type === TYPES.vendor) {
    global = data.allVendors;
    available = data.availableVendors;
    selected = data.selectedVendors;
    modified = data.modifiedVendors;
    modify = modifyVendors;
  } else if (type === TYPES.rating) {
    global = data.allRatings;
    available = data.availableRatings;
    selected = data.selectedRatings;
    modified = data.modifiedRatings;
    modify = modifyRatings;
  } else {
    data.colorFilters = setSelected(TYPES.color, data);
    data.sizeFilters = setSelected(TYPES.size, data);
    data.vendorFilters = setSelected(TYPES.vendor, data);
    data.ratingFilters = setSelected(TYPES.rating, data);
  }

  if (typeof global !== 'undefined') {
    len = global.length;

    for (i = 0; i < len; i += 1) {
      modified = modify(global[i]);
      disable = available.indexOf(global[i]) === -1;
      select = !disable && selected.indexOf(modified) !== -1;

      // redundant condition code
      if (select) {
        filters.isAtleastOneSelected = true;
      }

      filters.push({
        name: global[i],
        disabled: disable,
        selected: select,
        value: modified
      });
    }

    return filters;
  }

  return data;
};
/**
 * Toggle the filter selection based on user input.
 * @param type
 * @param filter
 */
const toggleFilter = (type, filter, collectionContextValue): void => {
  let key;
  let selected;
  let index;
  let data;
  const { collectionContext } = collectionContextValue;

  if (_.has(TYPES, type)) {
    key = 'selected' + capitalize(type) + 's';
    selected = collectionContext[key];
    index = _.indexOf(selected, filter);

    if (index < 0) {
      selected.push(filter);
    } else {
      selected.splice(index, 1);
    }

    data = {
      key: selected,
      pageIndex: 1
    };

    collectionContextValue.setCollectionContext({
      ...collectionContext,
      ...data
    });
  }
};
const getIsAIFilterEnabled = (
  globalContext: GlobalState
): boolean => globalContext.gb.liquid.settings.enable_ai_filters_flow;
/**
 * Use history.pushState to modify the url instead of page load.
 * Throws error in case pushState is not present.
 * https://developer.mozilla.org/en-US/docs/Web/API/History/pushState
 * @param {String} url - new url to be navigated.
 * @param {Object} CollectionContextValue - CollectionContextValue which contains the collection context.
 * @param {Object} globalContext - globalContext which contains the global context.
 * @param {Boolean} replaceHistory - flag to detrmine if history needs to be replaced.
 * @method historyURLModifier.
 */
const historyURLModifier = (
  url: string, CollectionContextValue, globalContext: GlobalState, replaceHistory = false
): void => {
  const isAIFilterEnabled = getIsAIFilterEnabled(globalContext);
  const { collectionContext } = CollectionContextValue;
  const pathWithParams = window.location.pathname + window.location.search;

  if (isAIFilterEnabled && (url === window.location.href || url === pathWithParams)) {
    return;
  }

  CollectionContextValue.setCollectionContext({
    ...collectionContext,
    prevUrl: window.location.href
  });
  globalContext.gb.utils.saveToStore('previousUrl', window.location.href);
  globalContext.gb.utils.saveToStore('currentUrl', url);

  if (replaceHistory) {
    window.history.replaceState({
      data: collectionContext
    }, null, url);
  } else {
    window.history.pushState({
      data: collectionContext
    }, null, url);
  }
};
/**
  * Checks if the handle contains filters and if so extracts it.
  * @returns Object with extracted params from handle.
  * @method handleContainsFilter.
*/
const handleContainsFilter = (): any => {
  const handle: any = getCollectionHandle();
  let match;
  let regex = new RegExp(DELIMITERS.size + DELIMITERS.global + '\\d+W?', 'gi');

  if (!handle) {
    return {
      match: '',
      remaining: '',
      filterType: ''
    };
  }

  match = handle.match(regex);

  if (match && match.length !== 0) {
    return {
      match: capitalize(match[0].replace(DELIMITERS.global, ' ')),
      remaining: capitalize(handle.replace(regex, '').replaceAll(DELIMITERS.global, ' ').trim()),
      filterType: 'size'
    };
  }

  regex = new RegExp('-collection$', 'gi');
  match = handle.match(regex);

  if (match && match.length !== 0) {
    return {
      remaining: 'Collection',
      match: capitalize(handle.replace(regex, '').replaceAll(DELIMITERS.global, ' ')),
      filterType: 'vendor'
    };
  }

  return {
    remaining: capitalize(handle.replaceAll(DELIMITERS.global, ' ')),
    match: '',
    filterType: ''
  };
};
const getCollectionTitle = (globalContext, data): string => {
  let handleObj = handleContainsFilter();
  let filters = urlParser(globalContext);
  let temp: any = [];
  let title = '';
  let i;
  let len;

  len = filters.sizes.length;

  for (i = 0; i < len; i += 1) {
    temp.push('Size ' + filters.sizes[i]);
  }

  if (handleObj.filterType === 'size') {
    temp.push(handleObj.match);
  }

  temp = temp.concat(filters.colors);
  temp = temp.concat(filters.vendors);

  if (handleObj.filterType === 'vendor') {
    temp.push(handleObj.match);
  }

  len = filters.ratings.length;

  for (i = 0; i < len; i += 1) {
    temp.push('Rating ' + filters.ratings[i]);
  }

  if (temp.length === 0) {
    title = '';
  } else {
    title = temp?.toString()?.replaceAll(',', ' ')?.replaceAll('_', ' ') + ' ';
  }

  title += (data.seoPageTitle || data.title || handleObj.remaining) + ' | ' + globalContext.liquid.settings.service_name;

  return capitalize(title.trim());
};
const pushGAEventForDisplayQCModal = (
  globalContext,
  isProductInfoSuccess,
  isSizeRecommendationSuccess,
  suggestedSize,
  loadTime,
  numberOfImages
): void => {
  const status = isProductInfoSuccess ? 'success' : 'failure';
  const recommendedSizeStatus = isSizeRecommendationSuccess ? 'true' : 'false';
  const recommendedSize = suggestedSize || 'none';
  const imageCount = numberOfImages || 0;
  const eventObj = {
    status: status,
    recommendedSizeStatus: recommendedSizeStatus,
    recommendedSize: recommendedSize,
    loadTime: loadTime,
    imageCount: imageCount
  };

  globalContext.gtm.pushEvent('Collection_SysDisplayQCModal', eventObj);
};
const pushGAEventForSizeAdvisorProvisioned = (
  globalContext,
  sizeAdvisorProvisioned
): void => {
  const eventObj = {
    sizeAdvisorProvisioned: sizeAdvisorProvisioned
  };

  globalContext.gtm.pushEvent('Collection_SysSizeAdvisorProvisioned', eventObj);
};
const setDefaultQuickClosetButtonsState = (): void => {
  const quickCloseElements = [].slice.call(document.getElementsByClassName('js-quick-closet-rtr-btn'));

  quickCloseElements.forEach((element) => {
    element.classList.remove('qc-active', 'qc-throbber');
  });
};
const seasonalRefreshHelper = (globalContext: GlobalState, options: any): void => {
  // eslint-disable-next-line no-unused-expressions
  globalContext?.seasonalUtils?.handleSeasonalRefresh(options);
};
// eslint-disable-next-line complexity
const showPage = (pageNumber): string => {
  let query: string = window.location.search;
  let newUrl: string = window.location.pathname.replace(query, '');
  let temp: string;
  let lastChar: string;
  let firstChar: string;

  if ((!query || query === '') && pageNumber !== 1) {
    query += '?pnum=' + pageNumber;
  } else if (query.indexOf('pnum=') > -1) {
    query = query.replace(/pnum=(\d+)/, function () {
      if (pageNumber === 1) {
        return '';
      }

      return 'pnum=' + pageNumber;
    });
    temp = query.substr(1);
    firstChar = temp.charAt(0);
    lastChar = temp.charAt(temp.length - 1);

    if (temp && ((lastChar === '?') || (lastChar === '&'))) {
      temp = temp.slice(0, temp.length - 1);
    }

    if (temp && ((firstChar === '?') || (firstChar === '&'))) {
      temp = temp.slice(1);
    }

    if (temp) {
      query = '?' + temp;
    } else {
      query = '';
    }
  } else if (pageNumber !== 1) {
    query += '&pnum=' + pageNumber;
  }

  newUrl += query;

  return newUrl;
};
/**
 * Get the current page number the user is viewing.
 * @return {Number} - the current page number.
 */
const getPageNumber = (domUtils): number => parseInt(domUtils.getURLParameter('pnum'), 10) || 1;
/**
 * Get the new page index based on new page size.
 * @param {Number} newPageSize - the new size selected by user.
 * @method getPageIndexOnPageSize.
*/
const getPageIndexOnPageSize = (pageSize, data): number => {
  let viewingProd = 1 + ((data.pageIndex > 1) ?
    (data.pageIndex - 1) * getPageSize(data) : 0);
  let newPageSize = pageSize || data.totalProducts;

  return Math.ceil(viewingProd / newPageSize);
};
/**
* Reset the page index based on new page size. To be used
* when the page size changed by user silently.
* @param {Number} newPageSize - the new size selected by user.
* @method resetPageIndex.
*/
const resetPageIndex = (pageSize, collectionContextValue, globalContext): void => {
  const { collectionContext } = collectionContextValue;
  let newPageSize = pageSize;
  let data;

  data = {
    pageSize: newPageSize,
    totalProducts: collectionContext.totalProducts,
    returnedProducts: collectionContext.returnedProducts,
    pageIndex: collectionContext.pageIndex,
    testingDFPInline: collectionContext.testingDFPInline,
    showInlineEditorialContent: collectionContext.showInlineEditorialContent
  };

  globalContext.gb.utils.saveToStore(STORAGE_KEYS.productsPerPage, newPageSize, false);
  correctPageSize(data);
  setTotalPages(data);
  setMaxShowAblePage(data);

  data.pageIndex = getPageIndexOnPageSize(newPageSize, data);

  collectionContextValue.setCollectionContext({
    ...collectionContext,
    ...data
  });
};

/**
 * dereference additional image urls from model
 * @param {Object} model - FacetedSearchModel from which images array is to be dereferenced.
 * @return {Object} modelwithoutImages - Object to be pushed into history
 * @method removeAdditionalImageUrlsFromModel
 */
function removeAdditionalImageUrlsFromModel(CollectionContextValue): void {
  const { collectionContext } = CollectionContextValue;
  let matchedProducts = collectionContext.matchedProducts;
  let lastNProducts;

  _.each(matchedProducts, function (key) {
    key.additionalImgUrls = [];
  });

  lastNProducts = _.takeRight(collectionContext.matchedProducts, 300);

  collectionContext.returnedProducts = lastNProducts.length;
  collectionContext.matchedProducts = lastNProducts;
  collectionContext.strippedStore = _.takeRight(collectionContext.strippedStore, 300);
  collectionContext.additionalImgs = [];

  CollectionContextValue.setCollectionContext({
    ...collectionContext,
    returnedProducts: lastNProducts.length,
    matchedProducts: lastNProducts,
    strippedStore: _.takeRight(collectionContext.strippedStore, 300),
    additionalImgs: []
  });
}

// AI Filters Utils
const searchFilterOptions = (
  e: React.KeyboardEvent<HTMLInputElement>, filterClass: string, globalContext: GlobalState
): void => {
  e.preventDefault();

  globalContext.domUtils.sortFunction({
    className: filterClass,
    value: e.currentTarget.value?.trim()?.toLowerCase()
  });
};
const modifyCheckboxes = (checkboxValues: string | string[], forBE: boolean): string | string[] => {
  if (_.isString(checkboxValues)) {
    if (forBE) {
      let decodedValues = (String(checkboxValues)?.toLowerCase())?.split('__')?.join('-');

      return decodedValues.split('_').join(' ');
    }

    let encodedValues = (String(checkboxValues)?.toLowerCase())?.split('-')?.join('__');

    return (encodedValues)?.split(' ')?.join('_');
  }

  if (_.isArray(checkboxValues)) {
    return _.map(checkboxValues, function (checkboxValue: string) {
      return modifyCheckboxes(checkboxValue, forBE) as string;
    });
  }

  return checkboxValues;
};
const getFilters = (globalContext: GlobalState, urlPath?: string): string => {
  let path = urlPath;

  // Temp change to save filters only if on collections page

  // eslint-disable-next-line no-useless-escape
  if (!window.location.href.match(/\/collections\/([^\/]*)/gi)) {
    path = path || window.location.pathname;

    if (path.split('/').length === 5) {
      return path.substring(path.lastIndexOf('/') + 1);
    }

    return '';
  }

  path = path || window.location.pathname;
  path = globalContext.gb.domUtils.getURLParameter('filters');

  if (path && path.split('?')[0]) {
    return decodeURI(path.split('?')[0]).split(' ').join('');
  }

  return '';
};
const extractFiltersFromUrl = (globalContext: GlobalState): selectedAIFilter => {
  let filterString = getFilters(globalContext);

  filterString = decodeURIComponent(filterString);

  if (filterString?.length === 0) {
    return {};
  }

  // Split the input string by dashes
  const parts = filterString?.split('-');
  // Use reduce to process the array and accumulate the filters
  const filters = parts?.reduce((acc, part, index) => {
    if (index % 2 === 0) {
      // Even index: key
      const key = part;

      // Initialize the array for this key if not already present
      if (!acc[key]) {
        acc[key] = [];
      }
    } else {
      // Odd index: value
      const key = parts[index - 1];

      if (acc[key]) {
        acc[key].push(part);
      }
    }

    return acc;
  }, {});

  return filters;
};
// eslint-disable-next-line consistent-return
const modifySizes = (sizes: string | string[], forBE: boolean): string | string[] => {
  if (_.isString(sizes)) {
    return forBE ? sizes : sizes.toLowerCase();
  }

  if (_.isArray(sizes)) {
    return _.map(sizes, function (size: string) {
      return modifySizes(size, forBE) as string;
    });
  }
};
const modifyFilterValue = (key: string, valueArray: string | string[], forBE: boolean): string | string[] => {
  if (key === 'colors') {
    return modifyColors(valueArray, forBE);
  }

  if (key === 'sizes') {
    return modifySizes(valueArray, forBE);
  }

  return modifyCheckboxes(valueArray, forBE);
};
const includeFilters = (query: Query, filters: selectedAIFilter): Query => {
  Object.entries(filters).forEach(([key, valueArray]) => {
    const mappedKey = MappedFilterValue[key] || capitalize(key);
    let newKey = `include${mappedKey}`;
    let newValueArray = modifyFilterValue(key, valueArray, true) as string[];

    query.query[newKey] = newValueArray;
  });

  return query;
};
const extractSortTypeFromUrl = (collectionContext: CollectionState): SortList => {
  const params = new URLSearchParams(window.location.search);
  const sortTypeParam = params.get('sortType') || '';
  const sortType = collectionContext?.sortList?.find(
    (item) => item?.key?.toLowerCase() === sortTypeParam?.toLowerCase());

  return sortType;
};
const getAppliedFiltersCount = (appliedFilters: selectedAIFilter): number => {
  const totalCount = Object.values(appliedFilters)?.reduce((total, filters) => total + filters?.length, 0);

  return totalCount;
};
const getAllowedUrlParams = (urlObj: URL): string => {
  const filteredParams = new URLSearchParams();
  const allowedParams = ['sortType', 'pnum'];

  allowedParams.forEach((param) => {
    if (urlObj?.searchParams?.has(param)) {
      filteredParams.set(param, urlObj?.searchParams?.get(param));
    }
  });

  return filteredParams?.toString();
};
const handleUpdateURL = (url: string, filters: selectedAIFilter): string => {
  const urlObj = new URL(url);
  const path = urlObj.href;
  const pathname = urlObj.pathname;
  const urlParams = getAllowedUrlParams(urlObj);
  let baseURL: string;
  let updatedURL: string;

  if (pathname.split('/').length === 5) {
    baseURL = path.substring(0, path.lastIndexOf('/'));
  } else if (path.indexOf('?') !== -1) {
    baseURL = path.substring(0, path.lastIndexOf('?'));
  } else {
    baseURL = path;
  }

  const filterParts = Object.entries(filters)?.map(([key, values]) => {
    const formattedValues = values?.map((value) => `${key}-${value}`).join('-');

    return formattedValues;
  });

  // Construct the final URL by joining the base URL with the filter parts
  if (filterParts?.length > 0) {
    updatedURL = `${baseURL}/${filterParts.join('-')}`;
  } else {
    updatedURL = baseURL;
  }

  return urlParams ? `${updatedURL}?${urlParams}` : updatedURL;
};
const handleUpdateUrlParams = (key: string, value: string): string => {
  const url = new URL(window.location.href);
  let params = new URLSearchParams(url.search);

  if (key && value === '') {
    // if empty value passed, remove the key
    params.delete(key);
  } else {
    // set new params
    params = new URLSearchParams();

    if (key && value) {
      // if both key and value are passed, add them as new params
      params.set(key, value);
    }
  }

  url.search = params.toString();

  return url.toString();
};
const getDetailedFilterListByFilterType = (detailedFilters: AIFilter[], filterType: string): string[] => {
  const detailedFiltersByType = detailedFilters?.find(
    (filter) => filter?.businessName?.toLowerCase() === filterType?.toLowerCase());
  const filterListByType = detailedFiltersByType?.activeValues && Object.keys(detailedFiltersByType?.activeValues);

  return filterListByType;
};
const syncFiltersWithApiResponse = (
  aiFiltersApplied: selectedAIFilter, detailedFilters: AIFilter[], globalContext: GlobalState, collectionContextValue
): selectedAIFilter => {
  let isFilterModified = false;
  const updatedAiFiltersApplied = Object.keys(aiFiltersApplied)?.reduce((updatedFilters, filterType) => {
    const filterListByType = getDetailedFilterListByFilterType(detailedFilters, filterType);
    const modifiedFilterListByType = filterListByType?.length > 0 ?
      modifyFilterValue(filterType, filterListByType, false) : [];
    const filteredValues = aiFiltersApplied[filterType]?.filter((value) => {
      if (!modifiedFilterListByType?.includes(value)) {
        isFilterModified = true;
      }

      return modifiedFilterListByType?.includes(value);
    });

    if (filteredValues?.length > 0) {
      updatedFilters[filterType] = filteredValues;
    } else {
      isFilterModified = true;
    }

    return updatedFilters;
  }, {});
  const url = handleUpdateURL(window.location.href, updatedAiFiltersApplied);

  historyURLModifier(url, collectionContextValue, globalContext, isFilterModified);

  return updatedAiFiltersApplied;
};
const handleUpdateFilters = (
  type: string, filters: string[], isAllClearClicked: boolean, aiFiltersApplied: selectedAIFilter
): selectedAIFilter => {
  let updatedFilters: selectedAIFilter;

  if (isAllClearClicked) {
    updatedFilters = {};
  } else {
    updatedFilters = aiFiltersApplied;

    if (filters?.length === 0) {
      delete updatedFilters[type];
    } else {
      updatedFilters[type] = filters;
    }
  }

  return updatedFilters;
};
const handleFilterClickGAEvents = (
  globalContext: GlobalState, type: string, filterValue: string, isFilterAlreadySelected: boolean, eventCategory: string
): void => {
  let trueFilterValue: string;

  if (type === 'colors' || type === 'sizes') {
    trueFilterValue = modifyFilterValue(type, filterValue, false) as string;
  } else {
    trueFilterValue = modifyFilterValue(type, filterValue, true) as string;
  }

  if (isFilterAlreadySelected) {
    collectionGAEvents(globalContext, eventCategory, 'unselectFilterFacet', `filterRemoved=${type}, filterValue=${trueFilterValue}`);
  } else {
    collectionGAEvents(globalContext, eventCategory, 'selectFilterFacet', `selectedFilterFacet=${type}, filterValue=${trueFilterValue}`);
  }
};
const createFilterLabelForGAEvent = (
  appliedFilters: selectedAIFilter, detailedFilters: AIFilter[], addSortToLabel = false, sortValue?: string
): string => {
  const allFilters = detailedFilters?.map((filter) => filter?.businessName?.toLowerCase());
  let sortLabel = addSortToLabel ? `sortByValue = ${sortValue || 'NA'}` : '';
  let filterByLabel = '';

  allFilters.forEach((filter) => {
    let modifiedFilterList = [];

    if (appliedFilters[filter] && filter !== 'colors') {
      modifiedFilterList = modifyFilterValue(filter, appliedFilters[filter], filter !== 'colors') as string[];
    } else {
      modifiedFilterList = appliedFilters[filter];
    }

    filterByLabel += `${filterByLabel ? ', ' : ''}${filter}Selected = <${modifiedFilterList ? modifiedFilterList?.join(',') : 'NA'}>`;
  });

  return sortLabel ? `${sortLabel}, ${filterByLabel}` : filterByLabel;
};
const getStickyHeight = (): string | number => {
  const headerMainEl: HTMLElement = document.querySelector('.header-main');
  const headerOffsetHeight = headerMainEl?.offsetTop || 0;
  const coachingBarEl = document.querySelector('.coaching-wrap');
  const coachingBarHeight = coachingBarEl?.clientHeight ? coachingBarEl?.clientHeight - 10 : 0;

  return headerMainEl ? headerMainEl.clientHeight + headerOffsetHeight + coachingBarHeight : null;
};
const handleClearAllFiltersClick = (globalContext: GlobalState, collectionContextValue): void => {
  const { collectionContext, setCollectionContext } = collectionContextValue;
  const sortValue = collectionContext?.sortTextCollection?.toLowerCase();
  const GAEventLabel = createFilterLabelForGAEvent(collectionContext.aiFiltersApplied,
    collectionContext.detailedFilters, true, sortValue);

  collectionGAEvents(globalContext, 'CollectionPage', 'clickFilterClearAllButton', GAEventLabel);

  const urlWithUpdatedParams = handleUpdateUrlParams('', '');
  const updatedFilters = handleUpdateFilters(null, [], true, collectionContext?.aiFiltersApplied);
  const url = handleUpdateURL(urlWithUpdatedParams, updatedFilters);

  removeAdditionalImageUrlsFromModel(collectionContextValue);
  historyURLModifier(url, collectionContextValue, globalContext);
  setCollectionContext((prevContext) => ({
    ...prevContext,
    sortType: '',
    sortTextCollection: '',
    isAIFiltersUpdated: true
  }));
};

export {
  STORAGE_KEYS,
  TYPES,
  AI_FILTER_KEYS,
  urlClearFilters,
  displayWait,
  removeWait,
  onFilterChange,
  urlParser,
  urlCutter,
  hasFilterSelected,
  clearFilter,
  handleEditorialData,
  setSelected,
  convertGBSizeToSize,
  modifyColors,
  modifyVendors,
  modifyRatings,
  convertSizeToGBSize,
  toggleFilter,
  historyURLModifier,
  removeAdditionalImageUrlsFromModel,
  getCollectionTitle,
  pushGAEventForDisplayQCModal,
  pushGAEventForSizeAdvisorProvisioned,
  handleContainsFilter,
  setDefaultQuickClosetButtonsState,
  seasonalRefreshHelper,
  setMaxShowAblePage,
  correctPageSize,
  setTotalPages,
  showPage,
  getPageNumber,
  resetPageIndex,
  searchFilterOptions,
  extractFiltersFromUrl,
  COLORS_TO_STYLES,
  includeFilters,
  modifyFilterValue,
  getFilters,
  extractSortTypeFromUrl,
  getAppliedFiltersCount,
  getAllowedUrlParams,
  handleUpdateURL,
  handleUpdateUrlParams,
  syncFiltersWithApiResponse,
  handleUpdateFilters,
  getDetailedFilterListByFilterType,
  createFilterLabelForGAEvent,
  handleFilterClickGAEvents,
  getStickyHeight,
  getIsAIFilterEnabled,
  handleClearAllFiltersClick
};
